<template>
    <div :class="wrapClasses">
        <input v-if="finalViewonly"
            readonly
            :class="viewonlyClasses"
            :value="textContent" />
        <div :class="[prefixCls + '-group-prepend']"
            v-if="prepend"
            v-show="slotReady && !finalViewonly">
            <slot name="prepend"></slot>
        </div>
        <i class="ivu-icon"
            v-if="clearable && maskedValue && !finalViewonly"
            :class="[
                'ivu-icon-ios-close-circle ', 
                prefixCls + '-icon', 
                prefixCls + '-icon-clear' , 
                prefixCls + '-icon-normal', 
                prefixCls + '-icon-append']"
            @click="handleClear">
        </i>
        <input-mask v-show="!finalViewonly"
            type="text"
            ref="inputMask"
            :class="inputClasses"
            :style="inputStyle"
            :value="maskedValue"
            :mask="mask"
            :name="name"
            :guide="guide"
            :placeholder="placeholder"
            :disabled="finalDisabled"
            :readonly="finalReadonly"
            :placeholderChar="placeholderChar"
            @input="handleInput"
            @keyup.enter="handleEnter"
            @keyup="handleKeyup"
            @keypress="handleKeypress"
            @keydown="handleKeydown"
            @focus="handleFocus"
            @blur="handleBlur"
            @change="handleChange">
        </input-mask>
        <div :class="[prefixCls + '-group-append']"
            v-if="append"
            v-show="slotReady && !finalViewonly">
            <slot name="append"></slot>
        </div>
    </div>
</template>
<script>
import { InputMask, conformToMask } from '../input-mask'
import Field from '../../mixins/field'
import { BigNumber } from 'bignumber.js'
import { type, getVNodeTextContent } from '../../utils/assist'
import numberMask from '../input-mask/number-mask'

const prefixCls = 'ivu-input';

export default {
    name: "InputMaskNumber",

    mixins: [Field],

    components: {
        InputMask
    },

    props: {
        value: {
            validator(v){
                var t = type(v)
                
                return t == "string" || t == "number" || t == "bignumber" || (v && v._isBigNumber)
            },
            default: ''
        },
        name: {
            type: String,
            default: ''
        },
        // 是否显示mask占位符，引导输入
        guide: {
            type: Boolean,
            default: false
        },
        // mask占位符
        placeholderChar: {
            type: String,
            default: '#'
        },
        size: {
            validator(value) {
                return oneOf(value, ['small', 'large', 'default']);
            }
        },
        clearable: {
            type: Boolean,
            default: false
        },
        // 空提示
        placeholder: {
            type: String,
            default: ''
        },
        // 前缀
        prefix: {
            type: String,
            default: ''
        },
        // 后缀
        suffix: {
            type: String,
            default: ''
        },
        // 是否包含千分位
        includeThousandsSeparator: {
            type: Boolean,
            default: true
        },
        // 千分位符号
        thousandsSeparatorSymbol: {
            type: String,
            default: ','
        },
        // 是否允许小数
        allowDecimal: {
            type: Boolean,
            default: true
        },
        // 小数点符号
        decimalSymbol: {
            type: String,
            default: '.'
        },
        // 小数最大长度
        decimalLimit: {
            type: Number,
            default: 2
        },
        // 小数位必填
        requireDecimal: {
            type: Boolean,
            default: false
        },
        // 是否允许负数
        allowNegative: {
            type: Boolean,
            default: true
        },
        // 是否允许前导0 todo
        allowLeadingZeroes: {
            type: Boolean,
            default: false
        },
        // 整数位最大长度
        integerLimit: {
            type: Number,
            default: 28
        },
        // 用零填充缺省的小数位
        fillDecimalWithZero: {
            type: Boolean,
            default: true
        },
        // 最小值
        min: {
            type: Number,
            default: NaN
        },
        // 最大值
        max: {
            type: Number,
            default: NaN
        },
        // input 样式控制
        inputStyle: {
            type: Object
        },
    },
    data() {
        return {
            // 决定input元素框如何显示
            maskedValue: this.format(this.value),
            prefixCls: prefixCls,
            prepend: true,
            append: true,
            slotReady: false,
            focus: false,
            textContent: '',
        }
    },
    computed: {
        wrapClasses() {
            return [
                `${prefixCls}-wrapper`,
                {
                    [`${prefixCls}-wrapper-${this.size}`]: !!this.size,
                    [`${prefixCls}-type`]: this.type,
                    [`${prefixCls}-group`]: this.prepend || this.append,
                    [`${prefixCls}-group-${this.size}`]: (this.prepend || this.append) && !!this.size,
                    [`${prefixCls}-group-with-prepend`]: this.prepend,
                    [`${prefixCls}-group-with-append`]: this.append,
                    [`${prefixCls}-hide-icon`]: this.append // #554
                }
            ];
        },
        inputClasses() {
            return [
                `${prefixCls}`,
                `${prefixCls}-mask-number`,
                {
                    [`${prefixCls}-${this.size}`]: !!this.size,
                    [`${prefixCls}-disabled`]: this.finalDisabled
                }
            ];
        },
        strValue() {
            return this.value + ''
        },
        viewonlyClasses(){
            return [
                `${prefixCls}-text-content`,
                {
                    [`is-${this.size}`]: !!this.size,
                }
            ]
        },
    },
    methods: {
        mask: numberMask,

        toBigNumber(value) {
            var rawValue = this.removeNonDigit(value + '')

            return new BigNumber(rawValue || 0)
        },

        handleClear() {
            const e = {
                value: '',
                oldValue: this.value
            };

            this.maskedValue = ''
            this.$emit('input', '');
            this.$emit('on-change', e);
        },

        handleEnter(event) {
            this.$emit('on-enter', event);
        },

        handleKeydown(event) {
            this.$emit('on-keydown', event);
        },

        handleKeypress(event) {
            this.$emit('on-keypress', event);
        },

        handleKeyup(event) {
            this.$emit('on-keyup', event);
        },

        handleFocus(event) {
            this.focus = true
            this.$emit('on-focus', event);
        },

        handleBlur(event) {
            this.focus = false
            // 格式化显示值
            this.maskedValue = this.format(this.maskedValue, true)
            this.updateMask(this.maskedValue)

            this.$emit('on-blur', event);
            this.dispatchFormBlur(this.value)
        },

        handleChange(event) {
            this.$emit('on-input-change', event)
        },

        handleInput(val) {
            var value = val,
                maskedValue = this.focus ? val : this.format(val, true)

            // 获取新值（排除格式化干扰）
            if (val != "") {
                var num = this.removeNonDigit(val),
                    hasDecimal = num.indexOf(".") >= 0,
                    [integer = "", fraction = ""] = num.split(".")

                value = integer
                if (hasDecimal) {
                    value += "." + fraction
                }
            }

            // [min, max]校验
            if (this.isOverflow(value) != 0) {
                return this.updateMask(this.maskedValue)
            }

            // masked-value
            if(this.maskedValue != maskedValue){
                this.maskedValue = maskedValue
            }

            // no-change, 因为有做事后变更，如min,max判断
            if (value === this.value) {
                return
            }

            // value change
            var e = {
                value: value,
                oldValue: this.value
            }

            this.$emit('input', value)
            this.$emit('on-change', e)
            this.dispatchFormChange(value)
        },

        isOverflow(value) {
            // 跳过空值
            if (value == "" || value == null || value == undefined) {
                return false
            }

            var curValue = this.toBigNumber(value)

            if (!isNaN(this.min)) {
                if (curValue.lt(this.min)) {
                    return -1
                }
            }

            if (!isNaN(this.max)) {
                if (curValue.gt(this.max)) {
                    return 1
                }
            }

            return 0
        },

        // 更新mask value
        updateMask(maskedValue) {
            this.$refs.inputMask.updateValue(maskedValue)
        },

        // 移除前缀、后缀
        removePrefixAndSuffix(maskedValue) {
            return maskedValue
                .replace(new RegExp(this.suffix + "$"), '')
                .replace(new RegExp("^" + this.prefix), '')
        },

        // 获取maskedValue中的数字部分
        removeNonDigit(maskedValue) {
            return this.removePrefixAndSuffix(maskedValue)
                .replace(new RegExp(this.thousandsSeparatorSymbol, "g"), '')
        },

        // get mask str
        toMaskVal(value) {
            return conformToMask(value + '', this.mask, { guide: false }).conformedValue
        },

        // 格式化value
        format(value, isMask) {
            var maskedValue = value

            // 需要先转化为mask string
            if (!isMask) {
                maskedValue = this.toMaskVal(value)
            }

            var rawValue = this.removePrefixAndSuffix(maskedValue)

            // "" 直接返回
            if (rawValue.length <= 0) {
                return ""
            }

            // 0补齐
            if (this.allowDecimal && this.fillDecimalWithZero) {
                // 去除后缀
                var indexOfDecimal = rawValue.indexOf(this.decimalSymbol)
                var integer = indexOfDecimal >= 0 ? rawValue.slice(0, indexOfDecimal) : rawValue
                var fraction = indexOfDecimal >= 0 ? rawValue.slice(indexOfDecimal + 1) : ""

                // 补整数位0
                if (integer.length <= 0) {
                    rawValue = "0" + rawValue
                }

                // 补小数点
                if (indexOfDecimal < 0) {
                    rawValue += this.decimalSymbol
                }

                // 补0
                for (var i = 0; i < this.decimalLimit - fraction.length; i++) {
                    rawValue += "0"
                }

                maskedValue = rawValue + this.suffix
            }

            return maskedValue
        },

        // 设置文本值
        setTextContent(){
            this.$nextTick(() => {
                var text = "",
                    { prepend, append } = this.$slots

                if(prepend){
                    text += getVNodeTextContent(prepend)
                }

                text += this.maskedValue

                if(append){
                    text += getVNodeTextContent(append)
                }

                this.textContent = text
            })
        },
    },

    mounted() {
        this.prepend = this.$slots.prepend !== undefined
        this.append = this.$slots.append !== undefined

        this.slotReady = true
        this.setTextContent()
    },

    watch: {
        value(val, oldVal) {
            var maskedValue = this.toMaskVal(val)

            // 更新maskInput
            this.updateMask(maskedValue)
            this.setTextContent()
        },
    }
}
</script>